import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import themeColors from "assets/theme/colors.js";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Fade ref={ref} {...props} />;
});

/**
 * Base component for warning dialogs.
 * Tight, professional layout: compact header with inline icon + title,
 * concise body, and flat action buttons. Shared by all warning/confirmation dialogs.
 */
const BaseWarningDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Warning",
  message,
  confirmText = "Continue",
  cancelText = "Cancel",
  // Optional middle action, for a choice that is neither cancel nor the
  // recommended one (e.g. "Solve anyway").
  secondaryText,
  onSecondary,
  secondaryDisabled = false,
  showDontShowAgain = false,
  dontShowAgain = false,
  onToggleDontShowAgain,
  children,
  maxWidth = "xs",
  titleId,
  descriptionId,
  confirmDisabled = false,
  confirmButtonSx = {},
  cancelButtonSx = {},
  ...other
}) => {
  const resolvedTitleId = titleId || "warning-dialog-title";
  const resolvedDescriptionId = descriptionId || "warning-dialog-description";

  const accent = themeColors.warning.main;

  const dialogPaperSx = {
    borderRadius: "10px",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
    border: `1px solid ${themeColors.gray[200]}`,
    overflow: "hidden",
    backgroundColor: themeColors.white.main,
  };

  const headerSx = {
    display: "flex",
    alignItems: "center",
    gap: "0.625rem",
    px: "1.25rem",
    py: "0.875rem",
    borderBottom: `1px solid ${themeColors.gray[200]}`,
    backgroundColor: themeColors.gray[100],
  };

  const iconBadgeSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "1.75rem",
    height: "1.75rem",
    borderRadius: "6px",
    backgroundColor: `${accent}1F`, // ~12% alpha
    color: accent,
    flexShrink: 0,
  };

  const titleSx = {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: themeColors.gray[900],
    letterSpacing: "-0.01em",
    lineHeight: 1.3,
    flex: 1,
    minWidth: 0,
  };

  const closeButtonSx = {
    color: themeColors.gray[500],
    p: "4px",
    "&:hover": {
      backgroundColor: themeColors.gray[200],
      color: themeColors.gray[800],
    },
  };

  const dialogContentSx = {
    px: "1.25rem",
    py: "1rem",
    backgroundColor: themeColors.white.main,
  };

  const dialogMessageSx = {
    fontSize: "0.875rem",
    color: themeColors.gray[700],
    lineHeight: 1.55,
    fontWeight: 400,
    m: 0,
  };

  const ignoreRowSx = {
    mt: "0.875rem",
    pt: "0.75rem",
    borderTop: `1px dashed ${themeColors.gray[200]}`,
    display: "flex",
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: accent,
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: accent,
      opacity: 0.5,
    },
  };

  const dialogActionsSx = {
    px: "1.25rem",
    py: "0.75rem",
    gap: "0.5rem",
    backgroundColor: themeColors.gray[100],
    borderTop: `1px solid ${themeColors.gray[200]}`,
  };

  const buttonBaseSx = {
    fontWeight: 600,
    fontSize: "0.8125rem",
    textTransform: "none",
    borderRadius: "6px",
    padding: "0.375rem 0.875rem",
    minHeight: "32px",
    lineHeight: 1.4,
    boxShadow: "none",
    transition: "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
  };

  const confirmButtonBaseSx = {
    ...buttonBaseSx,
    backgroundColor: accent,
    color: themeColors.white.main,
    "&:hover": {
      backgroundColor: themeColors.warning.dark || accent,
      boxShadow: "none",
    },
    "&:focus-visible": {
      outline: "none",
      boxShadow: `0 0 0 3px ${themeColors.warning.extraLight || `${accent}40`}`,
    },
    "&.Mui-disabled": {
      backgroundColor: themeColors.gray[300],
      color: themeColors.white.main,
    },
    ...confirmButtonSx,
  };

  const cancelButtonBaseSx = {
    ...buttonBaseSx,
    color: themeColors.gray[800],
    backgroundColor: themeColors.white.main,
    border: `1px solid ${themeColors.gray[300]}`,
    "&:hover": {
      backgroundColor: themeColors.gray[200],
      borderColor: themeColors.gray[400],
    },
    "&:focus-visible": {
      outline: "none",
      boxShadow: `0 0 0 3px ${themeColors.gray[200]}`,
    },
    ...cancelButtonSx,
  };

  return (
    <Dialog
      open={Boolean(open)}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-labelledby={resolvedTitleId}
      aria-describedby={message ? resolvedDescriptionId : undefined}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
      {...other}
    >
      <Box sx={headerSx}>
        <Box sx={iconBadgeSx} aria-hidden="true">
          <WarningAmberRoundedIcon sx={{ fontSize: "1.125rem" }} />
        </Box>
        <Typography id={resolvedTitleId} component="h2" sx={titleSx}>
          {title}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={closeButtonSx}
        >
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <DialogContent sx={dialogContentSx}>
        {message && (
          <Typography
            id={resolvedDescriptionId}
            component="p"
            sx={dialogMessageSx}
          >
            {message}
          </Typography>
        )}

        {children}

        {showDontShowAgain && (
          <Box sx={ignoreRowSx}>
            <FormControlLabel
              control={
                <Switch
                  checked={dontShowAgain}
                  onChange={onToggleDontShowAgain}
                  size="small"
                />
              }
              label={
                <Typography
                  sx={{
                    color: themeColors.gray[700],
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                  }}
                >
                  Don't show this again
                </Typography>
              }
              sx={{ mx: 0, gap: "0.5rem" }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} sx={cancelButtonBaseSx} disableRipple>
          {cancelText}
        </Button>
        {secondaryText && onSecondary && (
          <Button onClick={onSecondary} disabled={secondaryDisabled} sx={cancelButtonBaseSx} disableRipple>
            {secondaryText}
          </Button>
        )}
        {/* No confirm action (e.g. nothing can be done but go back): cancel only. */}
        {onConfirm && (
          <Button
            onClick={onConfirm}
            disabled={confirmDisabled}
            sx={confirmButtonBaseSx}
            disableElevation
          >
            {confirmText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

BaseWarningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Omit when there is nothing to confirm: only the cancel button is shown. */
  onConfirm: PropTypes.func,
  title: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  secondaryText: PropTypes.string,
  onSecondary: PropTypes.func,
  secondaryDisabled: PropTypes.bool,
  showDontShowAgain: PropTypes.bool,
  dontShowAgain: PropTypes.bool,
  onToggleDontShowAgain: PropTypes.func,
  children: PropTypes.node,
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", false]),
  titleId: PropTypes.string,
  descriptionId: PropTypes.string,
  confirmDisabled: PropTypes.bool,
  confirmButtonSx: PropTypes.object,
  cancelButtonSx: PropTypes.object,
};

export default BaseWarningDialog;
